import React, { useCallback, useEffect, useState } from "react";
import * as Icons from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import Documents from "@/pages/Documents";
import { DicrTab } from "./DicrTab";
import { LibraryTab } from "./LibraryTab";
import { CopyTab, CopyRequestForm } from "./CopyTab";
import { RolesTab } from "./RolesTab";

const PROCESSES = [
  { k: "dicr", icon: Icons.FilePen, title: "1. Documentation Request", flow: "Requestor → ADCO approval → Check & standardize → Polcom → Route" },
  { k: "library", icon: Icons.UploadCloud, title: "2. Document Upload", flow: "ADCO uploads, stamps & tags → Holdings DCO approves → Process Owner discusses → Employees acknowledge" },
  { k: "copy", icon: Icons.Copy, title: "3. Document Copy Request", flow: "ADRC → Immediate Head → DCO → unique expiring link" },
  { k: "dicr", icon: Icons.Archive, title: "3.1 Discontinuance", flow: "Requestor DICR → Immediate Head → DCO stamps Obsolete & sets archive period" },
];

const RoleChips = ({ roles, companies }) => {
  const code = (id) => companies.find((c) => c.id === id)?.code || companies.find((c) => c.id === id)?.name;
  const chips = [...roles.adco.map((c) => `ADCO · ${code(c)}`), ...roles.adrc.map((c) => `ADRC · ${code(c)}`), ...(roles.hdco ? ["Holdings DCO"] : []), ...(roles.admin ? ["Admin"] : [])];
  return <div className="flex flex-wrap gap-1" data-testid="dms-my-roles">{chips.length ? chips.map((c) => <span key={c} className="rounded-full bg-white/80 ring-1 ring-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600">{c}</span>) : <span className="text-[11px] text-slate-400">No DMS role — you can file requests and view documents tagged to you.</span>}</div>;
};

const PendingPopup = ({ pending, onOpen }) => {
  const [open, setOpen] = useState(false);
  const items = [...pending.discuss.map((p) => ({ ...p, kind: "Discuss with your team" })), ...pending.acknowledge.map((p) => ({ ...p, kind: "Acknowledge" }))];
  useEffect(() => { if (items.length) setOpen(true); }, [items.length]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid="dms-pending-popup">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Icons.BellRing className="h-5 w-5 text-amber-500" />Documents need your attention</DialogTitle></DialogHeader>
        <div className="space-y-2">{items.map((i) => <button key={i.id + i.kind} onClick={() => { setOpen(false); onOpen(i.id); }} className="w-full text-left rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50"><div className="text-sm font-medium text-slate-800">{i.title}</div><div className="text-[11px] text-slate-500">{i.no} · {i.kind}</div></button>)}</div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)} data-testid="dms-pending-later">Remind me later</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function DmsHome() {
  const [meta, setMeta] = useState(null);
  const [dicr, setDicr] = useState([]);
  const [lib, setLib] = useState([]);
  const [copy, setCopy] = useState([]);
  const [pending, setPending] = useState({ acknowledge: [], discuss: [] });
  const [tab, setTab] = useState("dicr");
  const [copyDoc, setCopyDoc] = useState(null);
  const [focus, setFocus] = useState(null);
  const { hasPerm } = useAuth();
  const reload = useCallback(() => {
    api.get("/dms/dicr").then(({ data }) => setDicr(data));
    api.get("/dms/library").then(({ data }) => setLib(data));
    api.get("/dms/copy").then(({ data }) => setCopy(data));
    api.get("/dms/pending").then(({ data }) => setPending(data));
  }, []);
  useEffect(() => { api.get("/dms/me").then(({ data }) => setMeta(data)); reload(); }, [reload]);
  if (!meta) return <div className="h-40 rounded-xl bg-white/60 animate-pulse" />;
  const count = { dicr: dicr.filter((d) => d.can_act).length, library: lib.filter((d) => d.can_review || d.can_discuss || d.can_ack || d.can_revise).length, copy: copy.filter((c) => c.can_act).length };
  return (
    <div className="uemp-fade-in">
      <PageHeader title="Document Management System" subtitle="Controlled documents — requests, approvals, posting, acknowledgement, copies and discontinuance" />
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4 mb-5" data-testid="dms-process-flow">
        <div className="flex flex-wrap items-center gap-2 mb-3"><Icons.Workflow className="h-4 w-4 text-blue-600" /><span className="text-sm font-semibold text-slate-800">Process flow</span><div className="ml-auto"><RoleChips roles={meta.roles} companies={meta.companies} /></div></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {PROCESSES.map(({ k, icon: I, title, flow }) => (
            <button key={title} onClick={() => setTab(k)} className="text-left rounded-xl border border-slate-200 bg-white p-3 transition-shadow hover:shadow-md" data-testid={`dms-process-${title.split(" ")[0].replace(".", "")}`}>
              <div className="flex items-center gap-2"><div className="h-8 w-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center"><I className="h-4 w-4" /></div><div className="text-sm font-semibold text-slate-800">{title}</div>{count[k] > 0 && <span className="ml-auto rounded-full bg-blue-600 text-white px-2 text-[10px]">{count[k]}</span>}</div>
              <p className="mt-2 text-[11px] leading-4 text-slate-500">{flow}</p>
            </button>
          ))}
        </div>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="dicr" data-testid="dms-tab-dicr">DICR{count.dicr ? ` (${count.dicr})` : ""}</TabsTrigger>
          <TabsTrigger value="library" data-testid="dms-tab-library">Document Library{count.library ? ` (${count.library})` : ""}</TabsTrigger>
          <TabsTrigger value="copy" data-testid="dms-tab-copy">Copy Requests{count.copy ? ` (${count.copy})` : ""}</TabsTrigger>
          {hasPerm("document.view") && <TabsTrigger value="records" data-testid="dms-tab-records">Existing Records</TabsTrigger>}
          {meta.roles.admin && <TabsTrigger value="roles" data-testid="dms-tab-roles">DMS Roles</TabsTrigger>}
        </TabsList>
        <TabsContent value="dicr"><DicrTab meta={meta} library={lib} rows={dicr} reload={reload} /></TabsContent>
        <TabsContent value="library"><LibraryTab meta={meta} rows={lib} dicrs={dicr} reload={reload} onCopy={setCopyDoc} focusId={focus} onFocused={() => setFocus(null)} /></TabsContent>
        <TabsContent value="copy"><CopyTab rows={copy} reload={reload} /></TabsContent>
        {hasPerm("document.view") && <TabsContent value="records"><Documents /></TabsContent>}
        {meta.roles.admin && <TabsContent value="roles"><RolesTab meta={meta} /></TabsContent>}
      </Tabs>
      <CopyRequestForm doc={copyDoc} setDoc={setCopyDoc} onDone={() => { reload(); setTab("copy"); }} />
      <PendingPopup pending={pending} onOpen={(id) => { setTab("library"); setFocus(id); }} />
    </div>
  );
}
